"use client";

import { useQuery } from "@tanstack/react-query";

export interface DomainScore {
  domain: string;
  aipvs: number;
  tier: 1 | 2 | 3 | 4;
  tierLabel: "Premium" | "Strong" | "Moderate" | "Limited";
}

/**
 * Fetches lightweight AIPVS scores for a list of domains using the public batch endpoint.
 * When brandDomain is provided, scores include brand-specific DTI, uplift, and category relevance.
 * Returns a Map keyed by domain for easy lookup.
 */
export function useDomainScores(domains: string[], brandDomain?: string) {
  const uniqueDomains = Array.from(new Set(domains)).sort();

  const query = useQuery({
    queryKey: ["public-domain-scores", brandDomain ?? "", ...uniqueDomains],
    queryFn: async () => {
      if (uniqueDomains.length === 0) return new Map<string, DomainScore>();

      const res = await fetch("/api/public/aipvs-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domains: uniqueDomains,
          ...(brandDomain ? { brandDomain } : {}),
        }),
      });

      if (!res.ok) return new Map<string, DomainScore>();

      const data = await res.json();
      const map = new Map<string, DomainScore>();
      for (const score of data.scores ?? []) {
        map.set(score.domain, score);
      }
      return map;
    },
    enabled: uniqueDomains.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  return query;
}
